import { useMemo, useRef } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls';

import type {
	InvitedAppShortcutAllowedSourceAppKey,
	InvitedAppShortcutAllowedTargetAppKey,
	InvitedAppShortcutLozengeState,
} from '../../../common/types';
import type { UseInvitedAppShortcutsMode } from '../types';

export const ANALYTICS_CHANNEL: FabricChannel.growth = FabricChannel.growth;

export const useInvitedAppAnalytics = (): {
	fireAppAccessRemoved: ({
		targetApps,
	}: {
		targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
	}) => void;
	fireEligibilityUpdateFailed: ({
		sourceApp,
		targetApps,
		error,
	}: {
		error: string;
		sourceApp: InvitedAppShortcutAllowedSourceAppKey;
		targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
	}) => void;
	fireEligibilityUpdateSucceeded: ({
		sourceApp,
		targetApps,
	}: {
		sourceApp: InvitedAppShortcutAllowedSourceAppKey;
		targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
	}) => void;
	/**
	 * Fire an analytics event when the feature is exposed.
	 *
	 * This event is deduplicated across sessions using a hashed key stored in local storage.
	 * If the combination of userId, cloudId, and isEnabled has not changed since the last time
	 * the event was fired, subsequent calls are suppressed — even after page reloads.
	 *
	 * When the event fires, the eligibility-checked storage key is also cleared so that
	 * eligibility is re-evaluated after the user enters the experiment.
	 *
	 * @param isEnabled - the experiment's isEnabled value
	 * @param userId - the current user's ID
	 * @param cloudId - the current cloud/site ID
	 */
	fireFeatureExposed: ({
		isEnabled,
		userId,
		cloudId,
		mode,
	}: {
		cloudId?: string;
		isEnabled: boolean;
		mode: UseInvitedAppShortcutsMode;
		userId?: string | null;
	}) => Promise<void>;
	fireFetchCollaboratorsAccessFailed: ({
		sourceApp,
		targetApps,
		error,
	}: {
		error: string;
		sourceApp: InvitedAppShortcutAllowedSourceAppKey;
		targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
	}) => void;
	fireFetchCollaboratorsAccessSucceeded: ({
		sourceApp,
		targetApps,
		collaborators,
	}: {
		collaborators: number;
		sourceApp: InvitedAppShortcutAllowedSourceAppKey;
		targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
	}) => void;
	fireFetchInvitedAppShortcutsTraitsFailed: ({
		sourceApp,
		targetApps,
	}: {
		sourceApp: InvitedAppShortcutAllowedSourceAppKey;
		targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
	}) => void;
	fireFetchInvitedAppShortcutsTraitsSucceeded: ({
		sourceApp,
		targetApps,
	}: {
		sourceApp: InvitedAppShortcutAllowedSourceAppKey;
		targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
	}) => void;
	fireInvitedAppShortcutsCacheRefreshFailed: ({
		targetApp,
		lozengeState,
		error,
		errorStack,
	}: {
		error: string;
		errorStack: string | undefined;
		lozengeState: InvitedAppShortcutLozengeState;
		targetApp: InvitedAppShortcutAllowedTargetAppKey;
	}) => void;
	/**
	 * `lozengeState` is the state the refresh asked GRS to cache. It is the frontend
	 * half of PUMA-2362's disagreement counter — without it, FE-observed refresh
	 * success cannot be correlated against GRS-side variant writes during the ramp.
	 */
	fireInvitedAppShortcutsCacheRefreshSucceeded: ({
		targetApp,
		lozengeState,
	}: {
		lozengeState: InvitedAppShortcutLozengeState;
		targetApp: InvitedAppShortcutAllowedTargetAppKey;
	}) => void;
	fireInvitedAppShortcutsContainerRenderFailed: ({
		targetApps,
		error,
		errorStack,
	}: {
		error: string;
		errorStack: string | undefined;
		targetApps: string[];
	}) => void;
	/**
	 * Fire an analytics event when eligibility for invited app shortcuts is checked.
	 *
	 * This event is deduplicated across sessions using a hashed key stored in local storage.
	 * If the combination of userId, cloudId, and ineligibilityReason has not changed since the
	 * last time the event was fired, subsequent calls are suppressed — even after page reloads.
	 *
	 * @param ineligibilityReason - the reason the user is ineligible
	 * @param userId - the current user's ID
	 * @param cloudId - the current cloud/site ID
	 */
	fireInvitedAppShortcutsEligibilityChecked: ({
		ineligibilityReason,
		userId,
		cloudId,
	}: {
		cloudId?: string;
		ineligibilityReason: string;
		userId?: string | null;
	}) => Promise<void>;
	fireLinkClickedAcceptInvite: ({ app }: { app: InvitedAppShortcutAllowedTargetAppKey }) => void;
	/**
	 * Fire an analytics event when the animated lozenge is first rendered for a user.
	 * This event is consumed by TAP to derive the `{app}_invited_app_shortcuts_first_impression` trait.
	 */
	fireViewedAnimatedLozenge: ({ app }: { app: InvitedAppShortcutAllowedTargetAppKey }) => void;
	fireViewedInvitedAppShortcutPopup: ({
		app,
		collaborators,
	}: {
		app: InvitedAppShortcutAllowedTargetAppKey;
		collaborators: number;
	}) => void;
	/**
	 * Fire an analytics event when the user views the invited app shortcuts.
	 */
	fireViewedInvitedAppShortcuts: (
		apps: InvitedAppShortcutAllowedTargetAppKey[],
		firstImpressedApps: InvitedAppShortcutAllowedTargetAppKey[],
	) => void;
	fireViewedMoreCollaboratorsTooltip: ({
		app,
		collaborators,
	}: {
		app: InvitedAppShortcutAllowedTargetAppKey;
		collaborators: number;
	}) => void;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isViewedInvitedAppShortcutsFired = useRef<boolean>(false);

	return useMemo(
		() => ({
			/**
			 * Fire an analytics event when the user views the invited app shortcuts.
			 */
			fireViewedInvitedAppShortcuts: (
				apps: InvitedAppShortcutAllowedTargetAppKey[],
				firstImpressedApps: InvitedAppShortcutAllowedTargetAppKey[],
			) => {
				if (isViewedInvitedAppShortcutsFired.current) {
					return;
				}

				createAnalyticsEvent({
					eventType: 'screen',
					name: 'invitedAppShortcuts',
					attributes: {
						apps,
						firstImpressedApps,
					},
				}).fire(ANALYTICS_CHANNEL);

				isViewedInvitedAppShortcutsFired.current = true;
			},

			fireViewedInvitedAppShortcutPopup: ({
				app,
				collaborators,
			}: {
				app: InvitedAppShortcutAllowedTargetAppKey;
				collaborators: number;
			}) => {
				createAnalyticsEvent({
					eventType: 'screen',
					name: 'invitedAppShortcutPopup',
					attributes: {
						app,
						collaborators,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			fireLinkClickedAcceptInvite: ({ app }: { app: InvitedAppShortcutAllowedTargetAppKey }) => {
				createAnalyticsEvent({
					eventType: 'ui',
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'acceptInvite',
					attributes: {
						app,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			fireViewedMoreCollaboratorsTooltip: ({
				app,
				collaborators,
			}: {
				app: InvitedAppShortcutAllowedTargetAppKey;
				collaborators: number;
			}) => {
				createAnalyticsEvent({
					eventType: 'screen',
					name: 'moreCollaboratorsTooltip',
					attributes: {
						app,
						collaborators,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			/**
			 * Fire an analytics event when eligibility for invited app shortcuts is checked.
			 *
			 * This event is deduplicated across sessions using a hashed key stored in local storage.
			 * If the combination of userId, cloudId, and ineligibilityReason has not changed since the
			 * last time the event was fired, subsequent calls are suppressed — even after page reloads.
			 *
			 * @param ineligibilityReason - the reason the user is ineligible
			 * @param userId - the current user's ID
			 * @param cloudId - the current cloud/site ID
			 */
			fireInvitedAppShortcutsEligibilityChecked: async ({
				ineligibilityReason,
				userId,
				cloudId,
			}: {
				cloudId?: string;
				ineligibilityReason: string;
				userId?: string | null;
			}) => {
				const rawCacheKey = `${userId ?? ''}:${cloudId ?? ''}:${ineligibilityReason}`;
				if (await hasAlreadyBeenFired(ELIGIBILITY_CHECKED_STORAGE_KEY, rawCacheKey)) {
					return;
				}
				createAnalyticsEvent({
					eventType: 'track',
					action: 'checked',
					actionSubject: 'eligibility',
					attributes: {
						flagKey: 'twcg-460-personalised-invite-notifications-m2',
						ineligibilityReason,
					},
				}).fire(ANALYTICS_CHANNEL);
				await markAsFired(ELIGIBILITY_CHECKED_STORAGE_KEY, rawCacheKey);
			},

			/**
			 * Fire an analytics event when the feature is exposed.
			 *
			 * This event is deduplicated across sessions using a hashed key stored in local storage.
			 * If the combination of userId, cloudId, and isEnabled has not changed since the last time
			 * the event was fired, subsequent calls are suppressed — even after page reloads.
			 *
			 * When the event fires, the eligibility-checked storage key is also cleared so that
			 * eligibility is re-evaluated after the user enters the experiment.
			 *
			 * @param isEnabled - the experiment's isEnabled value
			 * @param userId - the current user's ID
			 * @param cloudId - the current cloud/site ID
			 */
			fireFeatureExposed: async ({
				isEnabled,
				userId,
				cloudId,
				mode,
			}: {
				cloudId?: string;
				isEnabled: boolean;
				mode: UseInvitedAppShortcutsMode;
				userId?: string | null;
			}) => {
				// Include the current date (YYYY-MM-DD) so the event fires at most once per day
				const today = new Date().toISOString().split('T')[0];
				const rawCacheKey = `${userId ?? ''}:${cloudId ?? ''}:${String(isEnabled)}:${today}`;
				if (await hasAlreadyBeenFired(FEATURE_EXPOSED_STORAGE_KEY, rawCacheKey)) {
					return;
				}

				createAnalyticsEvent({
					eventType: 'track',
					action: 'exposed',
					actionSubject: 'feature',
					attributes: {
						featureKey:
							mode === 'experiment'
								? 'twcg-460-personalised-invite-notifications-m2'
								: 'twcg-460-preload-grs-invited-app-shortcuts',
						cohort: isEnabled ? 'variant' : 'control',
						mode,
					},
				}).fire(ANALYTICS_CHANNEL);

				await markAsFired(FEATURE_EXPOSED_STORAGE_KEY, rawCacheKey);
				// Clear the eligibility checked flag since the feature is now exposed,
				// so that the next eligibility check will fire.
				clearStorageKey(ELIGIBILITY_CHECKED_STORAGE_KEY);
			},

			fireFetchInvitedAppShortcutsTraitsSucceeded: ({
				sourceApp,
				targetApps,
			}: {
				sourceApp: InvitedAppShortcutAllowedSourceAppKey;
				targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
			}) => {
				createAnalyticsEvent({
					eventType: 'operational',
					action: 'succeeded',
					actionSubject: 'fetchInvitedAppShortcutsTraits',
					attributes: {
						sourceApp,
						targetApps,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			fireFetchInvitedAppShortcutsTraitsFailed: ({
				sourceApp,
				targetApps,
			}: {
				sourceApp: InvitedAppShortcutAllowedSourceAppKey;
				targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
			}) => {
				createAnalyticsEvent({
					eventType: 'operational',
					action: 'failed',
					actionSubject: 'fetchInvitedAppShortcutsTraits',
					attributes: {
						sourceApp,
						targetApps,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			fireFetchCollaboratorsAccessSucceeded: ({
				sourceApp,
				targetApps,
				collaborators,
			}: {
				collaborators: number;
				sourceApp: InvitedAppShortcutAllowedSourceAppKey;
				targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
			}) => {
				createAnalyticsEvent({
					eventType: 'operational',
					action: 'succeeded',
					actionSubject: 'fetchCollaboratorsAccess',
					attributes: {
						sourceApp,
						targetApps,
						collaborators,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			fireFetchCollaboratorsAccessFailed: ({
				sourceApp,
				targetApps,
				error,
			}: {
				error: string;
				sourceApp: InvitedAppShortcutAllowedSourceAppKey;
				targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
			}) => {
				createAnalyticsEvent({
					eventType: 'operational',
					action: 'failed',
					actionSubject: 'fetchCollaboratorsAccess',
					attributes: {
						sourceApp,
						targetApps,
						error,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			fireEligibilityUpdateSucceeded: ({
				sourceApp,
				targetApps,
			}: {
				sourceApp: InvitedAppShortcutAllowedSourceAppKey;
				targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
			}) => {
				createAnalyticsEvent({
					eventType: 'operational',
					action: 'succeeded',
					actionSubject: 'eligibilityUpdate',
					attributes: {
						sourceApp,
						targetApps,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			fireEligibilityUpdateFailed: ({
				sourceApp,
				targetApps,
				error,
			}: {
				error: string;
				sourceApp: InvitedAppShortcutAllowedSourceAppKey;
				targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
			}) => {
				createAnalyticsEvent({
					eventType: 'operational',
					action: 'failed',
					actionSubject: 'eligibilityUpdate',
					attributes: {
						sourceApp,
						targetApps,
						error,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			fireInvitedAppShortcutsCacheRefreshSucceeded: ({
				targetApp,
				lozengeState,
			}: {
				lozengeState: InvitedAppShortcutLozengeState;
				targetApp: InvitedAppShortcutAllowedTargetAppKey;
			}) => {
				createAnalyticsEvent({
					eventType: 'operational',
					action: 'succeeded',
					actionSubject: 'invitedAppShortcutsCacheRefresh',
					attributes: {
						targetApp,
						lozengeState,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			fireInvitedAppShortcutsCacheRefreshFailed: ({
				targetApp,
				lozengeState,
				error,
				errorStack,
			}: {
				error: string;
				errorStack: string | undefined;
				lozengeState: InvitedAppShortcutLozengeState;
				targetApp: InvitedAppShortcutAllowedTargetAppKey;
			}) => {
				createAnalyticsEvent({
					eventType: 'operational',
					action: 'failed',
					actionSubject: 'invitedAppShortcutsCacheRefresh',
					attributes: {
						targetApp,
						lozengeState,
						error,
						errorStack,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			fireInvitedAppShortcutsContainerRenderFailed: ({
				targetApps,
				error,
				errorStack,
			}: {
				error: string;
				errorStack: string | undefined;
				targetApps: string[];
			}) => {
				createAnalyticsEvent({
					eventType: 'operational',
					action: 'failed',
					actionSubject: 'invitedAppShortcutsContainerRender',
					attributes: {
						targetApps,
						error,
						errorStack,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			fireAppAccessRemoved: ({
				targetApps,
			}: {
				targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
			}) => {
				createAnalyticsEvent({
					eventType: 'track',
					action: 'removed',
					actionSubject: 'appAccess',
					attributes: {
						targetApps,
					},
				}).fire(ANALYTICS_CHANNEL);
			},

			fireViewedAnimatedLozenge: ({ app }: { app: InvitedAppShortcutAllowedTargetAppKey }) => {
				createAnalyticsEvent({
					eventType: 'screen',
					name: 'animatedLozenge',
					attributes: { app },
				}).fire(ANALYTICS_CHANNEL);
			},
		}),
		[createAnalyticsEvent],
	);
};

// ── Cross-session analytics dedup helpers ──────────────────────────────────────
//
// These helpers store a hashed fingerprint in local storage so that analytics
// events are only fired once per unique combination of parameters — even across
// page reloads. Each event type has its own storage key so they can be checked
// and cleared independently.

const ELIGIBILITY_CHECKED_STORAGE_KEY = 'invitedAppShortcuts.eligibilityChecked';
const FEATURE_EXPOSED_STORAGE_KEY = 'invitedAppShortcuts.featureExposed';

// In-memory cache of the latest hash written per analytics storage key.
// This preserves dedupe within the current page session even if localStorage fails.
const firedHashByStorageKey = new Map<string, string>();
// Circuit breaker for browser storage writes.
// Once a storage write throws (e.g. quota exceeded), we stop touching localStorage
// and fall back to in-memory dedupe only to avoid repeated runtime errors.
let shouldUseLocalStorage = true;

/**
 * Produces a non-reversible hash of the given raw string so we never store
 * plain-text identifiers in local storage.
 *
 * Prefers SHA-256 via the Web Crypto API when available, otherwise falls back
 * to a synchronous FNV-1a 32-bit hash for environments where the Web Crypto API
 * is not supported.
 *
 * @param raw - the raw string to hash
 * @returns a hex-encoded hash string
 */
async function hashString(raw: string): Promise<string> {
	// Primary path: SHA-256 via the Web Crypto API.
	// crypto.subtle is available in all modern browsers but requires a secure context (HTTPS).
	// It may be unavailable in:
	//   - Non-HTTPS pages (e.g. http://localhost without a secure context flag)
	//   - SSR / Node environments where the Web Crypto API is not globally available
	//   - Very old browsers (pre-Chrome 37, pre-Firefox 34, pre-Safari 11)
	if (typeof crypto !== 'undefined' && crypto.subtle) {
		try {
			const data = new TextEncoder().encode(raw);
			const hashBuffer = await crypto.subtle.digest('SHA-256', data);
			const hashArray = Array.from(new Uint8Array(hashBuffer));
			return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
		} catch {
			// Fall through to the fallback hash
		}
	}

	// Fallback: FNV-1a 32-bit hash.
	// Not cryptographically secure, but sufficient to avoid storing plain-text
	// identifiers in local storage. This path is only reached when the Web Crypto
	// API is unavailable (see conditions above) or throws unexpectedly.
	let hash = 0x811c9dc5;
	for (let i = 0; i < raw.length; i++) {
		hash ^= raw.charCodeAt(i);
		hash = Math.imul(hash, 0x01000193);
	}
	return (hash >>> 0).toString(16);
}

/**
 * Checks whether an analytics event has already been fired for the given
 * parameters by comparing a hashed fingerprint stored under `storageKey`.
 *
 * @param storageKey - the local storage key to read from
 * @param raw - the raw string representing the current parameter combination
 * @returns `true` if the stored hash matches, meaning the event should be suppressed.
 *          Returns `false` on any storage error.
 */
async function hasAlreadyBeenFired(storageKey: string, raw: string): Promise<boolean> {
	try {
		const hashed = await hashString(raw);
		if (firedHashByStorageKey.get(storageKey) === hashed) {
			return true;
		}

		if (!shouldUseLocalStorage) {
			return false;
		}

		const stored = AtlBrowserStorageLocal.getItem(storageKey);
		return stored === hashed;
	} catch {
		return false;
	}
}

/**
 * Persists a hashed fingerprint so that future calls to {@link hasAlreadyBeenFired}
 * with the same `storageKey` and `raw` value will return `true`.
 *
 * Storage errors (e.g. private browsing, quota exceeded) are silently ignored.
 *
 * @param storageKey - the local storage key to write to
 * @param raw - the raw string representing the current parameter combination
 */
async function markAsFired(storageKey: string, raw: string): Promise<void> {
	try {
		const hashed = await hashString(raw);
		// Always keep an in-memory copy so we can still dedupe within a page session
		// even when browser storage is unavailable or full.
		firedHashByStorageKey.set(storageKey, hashed);
		if (!shouldUseLocalStorage) {
			return;
		}
		await AtlBrowserStorageLocal.setItem(storageKey, hashed);
	} catch {
		// Storage quota/state can change at runtime (e.g. low storage, private mode).
		// Disable future localStorage writes to avoid repeated runtime errors.
		shouldUseLocalStorage = false;
		// Silently ignore storage errors (e.g. private browsing, quota exceeded)
	}
}

/**
 * Removes the stored fingerprint for the given storage key, allowing the
 * corresponding event to fire again regardless of previous parameters.
 *
 * @param storageKey - the local storage key to clear
 */
function clearStorageKey(storageKey: string): void {
	try {
		firedHashByStorageKey.delete(storageKey);
		if (!shouldUseLocalStorage) {
			return;
		}
		AtlBrowserStorageLocal.removeItem(storageKey);
	} catch {
		// Silently ignore storage errors
	}
}
